/* eslint-disable @typescript-eslint/no-explicit-any */
import { formatDate } from '@fullcalendar/core/index.js'
import Excel from 'exceljs'
import { saveAs } from 'file-saver'

import { MetadataContextInterface } from '../../../store/metadata-context-provider'
import {
  ALL_SELECT_OPTION,
  ALL_SELECT_OPTION_STRING,
  ALL_SELECT_VALUE_STRING,
  NONE_SELECT_OPTION_STRING,
} from '../../../utils/constants/global-constants'
import { Dropdown, optionsTypes } from '../../../utils/controls/select-options-data'
import { mpfNotifyHttp } from '../api/mpf-notify-http'
import { mpfRListsHttp } from '../api/mpf-r-lists-http'
import { UploadFiles, getMetadata } from '../data/files/files'
import { PascodeMetadata } from '../data/pascode-metadata'
import { convertDate } from '../date-and-time/date-and-time'

/** scrollToTop:
 * This function scrolls to the top of the page.
 * Note: Id can change for each project.
 * @param {string} behavior Determines the speed of scroll.
 */

type FilteredDatesType =
  | 'Last 3 days'
  | 'Last 7 days'
  | 'Last 15 days'
  | 'Last 24 hours'
  | 'Last 30 days'
  | 'Last 48 hours'
  | 'More than 30 days'
  | 'Next 1 days'
  | 'Next 2 days'
  | 'Next 3 days'
  | 'Next 4 days'
  | 'Next 5 days'
  | 'Next 6 days'

const FILE_CHARACTERS_UNALLOWED = [
  ',',
  '!',
  '@',
  '#',
  '$',
  '%',
  '^',
  '&',
  '*',
  '(',
  ')',
  '+',
  '=',
  '[',
  ']',
  '{',
  '}',
  'ʻ',
  ':',
  '“',
  '|',
  '<',
  '>',
  '/',
  '?',
  '~',
]
const scrollToTop = () => {
  document.getElementById('scroll')?.scrollIntoView({ behavior: 'smooth' })
}


const nextDayOfWeek = (d: any, dow: number, interval: number) => {
    d.setDate(d.getDate() + (dow+((7*interval)-d.getDay())) % (7*interval))

    return d.getDate()
}

/**
 * Generates recurring event instances based on recurrence rules (RRules) and event data.
 *
 * This function handles different recurrence patterns:
 * - Weekly events: Creates events that repeat on specified days of the week
 * - Monthly events: Creates events that repeat on specific days of the month or specific positions (e.g., first Monday)
 * - Yearly events: Creates events that repeat annually on specific months/days
 *
 * Each pattern supports two types of end conditions:
 * - endAfterCount: Repeats for a specific number of occurrences
 * - endBy: Repeats until a specific end date
 *
 * @param RRules - Object containing recurrence rules (frequency, start date, end conditions, etc.)
 * @param event - Original event object containing event details (title, description, location, etc.)
 * @param metadataContext - Context object containing metadata like MAJCOM and section options
 * @returns Array of recurring event objects formatted for calendar display
 */
const getRecurringEventData = (RRules, event, metadataContext) => {
  let recurringEvents: any[] = []

  if (RRules) {
    const daysOfWeek: number[] = []
    if (RRules?.byweekday?.length > 0) {
      RRules.byweekday.forEach((day) => {
        switch (day) {
          case 'su':
            daysOfWeek.push(0)
            break
          case 'mo':
            daysOfWeek.push(1)
            break
          case 'tu':
            daysOfWeek.push(2)
            break
          case 'we':
            daysOfWeek.push(3)
            break
          case 'th':
            daysOfWeek.push(4)
            break
          case 'fr':
            daysOfWeek.push(5)
            break
          case 'sa':
            daysOfWeek.push(6)
            break
        }
      })

      RRules.daysOfWeek = daysOfWeek
    }

    if (RRules?.freq) {
      const newRecurringEvents: any[] = []
      let count = 1 // Used when there is a recurring count
      let currentStartDate: any =
        RRules?.freq &&
        new Date(
          new Date(RRules?.dtstart).getFullYear(),
          new Date(RRules?.dtstart).getMonth(),
          new Date(RRules?.dtstart).getDate()
        )
      let currentEndDate: any =
        RRules?.freq &&
        new Date(
          new Date(RRules?.dtstart).getFullYear(),
          new Date(RRules?.dtstart).getMonth(),
          new Date(RRules?.dtstart).getDate() + 7,
          23,
          59,
          59
        )
      if (currentEndDate.getMonth() !== currentStartDate.getMonth()) {
        currentEndDate = new Date(
          new Date(RRules?.dtstart).getFullYear(),
          new Date(RRules?.dtstart).getMonth() + 1,
          0,
          23,
          59,
          59
        )
      }
      // switch case to handle logic for recurring events
      switch (RRules?.freq) {
        case 'weekly':
          switch (RRules?.endRadioSelection) {
            // if the event is based on count
            case 'endAfterCount':
              while (count <= RRules?.count) {
                if (currentStartDate >= new Date(RRules?.dtstart)) {
                  // add a new recurring event for each count
                  newRecurringEvents.push({
                    allDay: event.IsAllDayEvent,
                    daysOfWeek: RRules?.daysOfWeek ? RRules.daysOfWeek : undefined,
                    description: event.Description,
                    end: currentEndDate.toISOString(),
                    endRecur: currentEndDate.toISOString(),
                    etag: event.eTag,
                    eventRRule: event.RRule,
                    extendedProps: {
                      Category: event.Category,
                      IsArchived: event.IsArchived,
                      MAJCOM:
                        metadataContext?.majcomOptions.find(
                          (option) => option.value === event?.MAJCOMCode
                        )?.label || 'Unknown',
                      Section:
                        metadataContext?.sectionOptions.find(
                          (option) => option.value === event?.SectionCode
                        )?.label || 'Unknown',
                    },
                    id: event.eKey,
                    rRule: RRules,
                    start: currentStartDate.toISOString(),
                    startRecur: currentStartDate.toISOString(),
                    title: event.Title,
                  })
                }

                // increase the current start date by a week
                currentStartDate = new Date(
                  new Date(currentStartDate).getFullYear(),
                  new Date(currentStartDate).getMonth(),
                  new Date(currentStartDate).getDate() + 7
                )

                // increase the current end date by a week
                currentEndDate = new Date(
                  new Date(currentEndDate).getFullYear(),
                  new Date(currentEndDate).getMonth(),
                  new Date(currentEndDate).getDate() + 7,
                  23,
                  59,
                  59
                )
                if (currentStartDate >= new Date(RRules?.dtstart)) count++
              }
              break
            // if the event is based on date
            case 'endBy':
            case 'noEndDate':
              while (currentStartDate <= new Date(RRules?.until)) {
                if (currentStartDate >= new Date(RRules?.dtstart)) {
                  // add a new recurring event for each count
                  newRecurringEvents.push({
                    allDay: event.IsAllDayEvent,
                    daysOfWeek: RRules?.daysOfWeek ? RRules.daysOfWeek : undefined,
                    description: event.Description,
                    end: currentEndDate.toISOString(),
                    endRecur: currentEndDate.toISOString(),
                    etag: event.eTag,
                    eventRRule: event.RRule,
                    extendedProps: {
                      Category: event.Category,
                      IsArchived: event.IsArchived,
                      MAJCOM:
                        metadataContext?.majcomOptions.find(
                          (option) => option.value === event?.MAJCOMCode
                        )?.label || 'Unknown',
                      Section:
                        metadataContext?.sectionOptions.find(
                          (option) => option.value === event?.SectionCode
                        )?.label || 'Unknown',
                    },
                    id: event.eKey,
                    rRule: RRules,
                    start: currentStartDate.toISOString(),
                    startRecur: currentStartDate.toISOString(),
                    title: event.Title,
                  })

                  if (RRules.bymonth) {
                    // increase the current start date by a week
                    currentStartDate = new Date(
                      new Date(currentStartDate).getFullYear(),
                      new Date(currentStartDate).getMonth() + RRules.bymonth,
                      new Date(currentStartDate).getDate()
                    )

                    // increase the current end date by a week
                    currentEndDate = new Date(
                      new Date(currentEndDate).getFullYear(),
                      new Date(currentEndDate).getMonth() + RRules.bymonth,
                      new Date(currentEndDate).getDate(),
                      23,
                      59,
                      59
                    )
                  } else {
                    const nextSundayStart = nextDayOfWeek(
                      new Date(currentStartDate), 0, RRules.interval)

                    let increaseMonthStart = 0
                    if (new Date(
                      new Date(currentStartDate).getFullYear(),
                      new Date(currentStartDate).getMonth(),
                      nextSundayStart,
                    ) <= new Date(currentStartDate)) increaseMonthStart++

                    const nextSaturdayEnd = nextDayOfWeek(new Date(
                      new Date(currentEndDate).getFullYear(),
                      new Date(currentEndDate).getMonth(),
                      new Date(currentEndDate).getDate() + 7, 
                      23,
                      59,
                      59
                    ), 6, RRules.interval)


                    let increaseMonthEnd = 0
                    if (new Date(
                      new Date(currentEndDate).getFullYear(),
                      new Date(currentEndDate).getMonth(),
                      nextSaturdayEnd, 
                      23,
                      59,
                      59
                    ) <= new Date(currentEndDate)) increaseMonthEnd++

                    // increase the current start date by a week
                    currentStartDate = new Date(
                      new Date(currentStartDate).getFullYear(),
                      new Date(currentStartDate).getMonth() + increaseMonthStart,
                      nextSundayStart,
                    )

                    // increase the current end date by a week
                    currentEndDate = new Date(
                      new Date(currentEndDate).getFullYear(),
                      new Date(currentEndDate).getMonth() + increaseMonthEnd,
                      RRules.interval ? (nextSaturdayEnd * RRules.interval) - 1 : 
                      nextSaturdayEnd - 1, 
                      23,
                      59,
                      59
                    )
                  }
                } 
              }
              break
          }
          break
        case 'monthly':
          if (RRules.bymonthday || RRules.bysetpos) {
            // If last day is selected
            if (RRules.bysetpos <= 0) {
              currentStartDate = new Date(
                new Date(currentStartDate).getFullYear(),
                new Date(currentStartDate).getMonth() + 2,
                RRules.bysetpos - 5
              )

              currentEndDate = new Date(
                new Date(currentEndDate).getFullYear(),
                new Date(currentEndDate).getMonth() + 2,
                RRules.bysetpos + 1,
                23,
                59,
                59
              )
            } else {
              currentStartDate = new Date(
                new Date(currentStartDate).getFullYear(),
                new Date(currentStartDate).getMonth(),
                RRules.bymonthday || RRules.bysetpos
              )

              currentEndDate = new Date(
                new Date(currentEndDate).getFullYear(),
                new Date(currentEndDate).getMonth(),
                RRules.bymonthday + 1 || RRules.bysetpos + 1,
                23,
                59,
                59
              )
            }
          }
          switch (RRules?.endRadioSelection) {
            // if the event is based on count
            case 'endAfterCount':
              while (count <= RRules?.count) {
                if (currentStartDate >= new Date(RRules?.dtstart)) {
                  // add a new recurring event for each count
                  newRecurringEvents.push({
                    allDay: event.IsAllDayEvent,
                    daysOfWeek: RRules?.daysOfWeek ? RRules.daysOfWeek : undefined,
                    description: event.Description,
                    end: currentEndDate.toISOString(),
                    endRecur: currentEndDate.toISOString(),
                    etag: event.eTag,
                    eventRRule: event.RRule,
                    extendedProps: {
                      Category: event.Category,
                      IsArchived: event.IsArchived,
                      MAJCOM:
                        metadataContext?.majcomOptions.find(
                          (option) => option.value === event?.MAJCOMCode
                        )?.label || 'Unknown',
                      Section:
                        metadataContext?.sectionOptions.find(
                          (option) => option.value === event?.SectionCode
                        )?.label || 'Unknown',
                    },
                    id: event.eKey,
                    rRule: RRules,
                    start: currentStartDate.toISOString(),
                    startRecur: currentStartDate.toISOString(),
                    title: event.Title,
                  })
                }

                if (RRules.bymonth) {
                  // increase the current start date by the number month
                  currentStartDate = new Date(
                    new Date(currentStartDate).getFullYear(),
                    new Date(currentStartDate).getMonth() + RRules.bymonth,
                    new Date(currentStartDate).getDate()
                  )

                  // increase the current end date by the number month
                  currentEndDate = new Date(
                    new Date(currentEndDate).getFullYear(),
                    new Date(currentEndDate).getMonth() + RRules.bymonth,
                    new Date(currentEndDate).getDate()
                  )
                } else if (RRules.bymonthday) {
                  // increase the current start date by a month and the number of days
                  currentStartDate = new Date(
                    new Date(currentStartDate).getFullYear(),
                    new Date(currentStartDate).getMonth() + RRules.interval,
                    RRules.bymonthday
                  )

                  // increase the current end date by a month and the number of days
                  currentEndDate = new Date(
                    new Date(currentEndDate).getFullYear(),
                    new Date(currentEndDate).getMonth() + RRules.interval,
                    RRules.bymonthday + 1
                  )
                }
                if (currentStartDate >= new Date(RRules?.dtstart)) count++
              }
              break
            // if the event is based on date
            case 'endBy':
            case 'noEndDate':
              while (currentStartDate <= new Date(RRules?.until)) {
                if (currentStartDate >= new Date(RRules?.dtstart)) {
                  // add a new recurring event for each count
                  newRecurringEvents.push({
                    allDay: event.IsAllDayEvent,
                    daysOfWeek: RRules?.daysOfWeek ? RRules.daysOfWeek : undefined,
                    description: event.Description,
                    end: currentEndDate.toISOString(),
                    endRecur: currentEndDate.toISOString(),
                    etag: event.eTag,
                    eventRRule: event.RRule,
                    extendedProps: {
                      Category: event.Category,
                      IsArchived: event.IsArchived,
                      MAJCOM:
                        metadataContext?.majcomOptions.find(
                          (option) => option.value === event?.MAJCOMCode
                        )?.label || 'Unknown',
                      Section:
                        metadataContext?.sectionOptions.find(
                          (option) => option.value === event?.SectionCode
                        )?.label || 'Unknown',
                    },
                    id: event.eKey,
                    rRule: RRules,
                    start: currentStartDate.toISOString(),
                    startRecur: currentStartDate.toISOString(),
                    title: event.Title,
                  })
                }
                if (RRules.bymonth) {
                  // increase the current start date by the number month
                  currentStartDate = new Date(
                    new Date(currentStartDate).getFullYear(),
                    new Date(currentStartDate).getMonth() + RRules.bymonth,
                    new Date(currentStartDate).getDate()
                  )

                  // increase the current end date by the number month
                  currentEndDate = new Date(
                    new Date(currentEndDate).getFullYear(),
                    new Date(currentEndDate).getMonth() + RRules.bymonth,
                    new Date(currentEndDate).getDate()
                  )
                } else if (RRules.bymonthday) {
                  // increase the current start date by a month and the number of days
                  currentStartDate = new Date(
                    new Date(currentStartDate).getFullYear(),
                    new Date(currentStartDate).getMonth() + RRules.interval,
                    RRules.bymonthday
                  )

                  // increase the current end date by a month and the number of days
                  currentEndDate = new Date(
                    new Date(currentEndDate).getFullYear(),
                    new Date(currentEndDate).getMonth() + +RRules.interval,
                    RRules.bymonthday + 1
                  )
                } else if (RRules.bysetpos) {
                  if (RRules.bysetpos <= 0) {
                    // Get the start of the last week that is relevant for each monthly occurence
                    currentStartDate = new Date(
                      new Date(currentStartDate).getFullYear(),
                      new Date(currentStartDate).getMonth() + 2,
                      RRules.bysetpos - 5
                    )

                    // Get the end of the last week that is relevant for each monthly occurence
                    currentEndDate = new Date(
                      new Date(currentEndDate).getFullYear(),
                      new Date(currentEndDate).getMonth() + 2,
                      RRules.bysetpos + 1
                    )
                  } else {
                    // Get the start of the first week that is relevant for each monthly occurence
                    currentStartDate = new Date(
                      new Date(currentStartDate).getFullYear(),
                      new Date(currentStartDate).getMonth() + 1,
                      RRules.bysetpos * 7 - 6
                    )

                    // Get the end of the first week that is relevant for each monthly occurence
                    currentEndDate = new Date(
                      new Date(currentEndDate).getFullYear(),
                      new Date(currentEndDate).getMonth() + 1,
                      RRules.bysetpos * 7
                    )
                  }
                }
              }
              break
          }
          break
        case 'yearly':
          if (RRules.bymonth && RRules.bymonthday) {
            // increase the current start date by the number month
            currentStartDate = new Date(
              new Date(currentStartDate).getFullYear(),
              RRules.bymonth - 1,
              RRules.bymonthday
            )

            // increase the current end date by the number month
            currentEndDate = new Date(
              new Date(currentEndDate).getFullYear(),
              RRules.bymonth - 1,
              RRules.bymonthday,
              23,
              59,
              59
            )
          } else if (RRules.bymonth && RRules.bysetpos) {
            // increase the current start date by the number month
            currentStartDate = new Date(
              new Date(currentStartDate).getFullYear(),
              RRules.bymonth - 1,
              RRules.bysetpos * 7 - 6
            )

            // increase the current end date by the number month
            currentEndDate = new Date(
              new Date(currentEndDate).getFullYear(),
              RRules.bymonth - 1,
              RRules.bysetpos * 7,
              23,
              59,
              59
            )
          } else if (RRules.bymonth) {
            // increase the current start date by the number month
            currentStartDate = new Date(
              new Date(currentStartDate).getFullYear(),
              RRules.bymonth - 1,
              new Date(currentStartDate).getDate()
            )

            // increase the current end date by the number month
            currentEndDate = new Date(
              new Date(currentEndDate).getFullYear(),
              RRules.bymonth - 1,
              new Date(currentEndDate).getDate()
            )
          }
          switch (RRules?.endRadioSelection) {
            // if the event is based on count
            case 'endAfterCount':
              while (count <= RRules?.count) {
                if (currentStartDate >= new Date(RRules?.dtstart)) {
                  // add a new recurring event for each count
                  newRecurringEvents.push({
                    allDay: event.IsAllDayEvent,
                    daysOfWeek: RRules?.daysOfWeek ? RRules.daysOfWeek : undefined,
                    description: event.Description,
                    end: currentEndDate.toISOString(),
                    endRecur: currentEndDate.toISOString(),
                    etag: event.eTag,
                    eventRRule: event.RRule,
                    extendedProps: {
                      Category: event.Category,
                      IsArchived: event.IsArchived,
                      MAJCOM:
                        metadataContext?.majcomOptions.find(
                          (option) => option.value === event?.MAJCOMCode
                        )?.label || 'Unknown',
                      Section:
                        metadataContext?.sectionOptions.find(
                          (option) => option.value === event?.SectionCode
                        )?.label || 'Unknown',
                    },
                    id: event.eKey,
                    rRule: RRules,
                    start: currentStartDate.toISOString(),
                    startRecur: currentStartDate.toISOString(),
                    title: event.Title,
                  })
                }

                if (RRules.bymonth && RRules.bymonthday) {
                  // increase the current start date by the number month
                  currentStartDate = new Date(
                    new Date(currentStartDate).getFullYear() + 1,
                    new Date(currentStartDate).getMonth(),
                    RRules.bymonthday
                  )

                  // increase the current end date by the number month
                  currentEndDate = new Date(
                    new Date(currentEndDate).getFullYear() + 1,
                    new Date(currentEndDate).getMonth(),
                    RRules.bymonthday,
                    23,
                    59,
                    59
                  )
                } else if (RRules.bymonth) {
                  // increase the current start date by the number month
                  currentStartDate = new Date(
                    new Date(currentStartDate).getFullYear() + 1,
                    new Date(currentStartDate).getMonth(),
                    RRules.bysetpos * 7 - 6
                  )

                  // increase the current end date by the number month
                  currentEndDate = new Date(
                    new Date(currentEndDate).getFullYear() + 1,
                    new Date(currentEndDate).getMonth(),
                    RRules.bysetpos * 7,
                    23,
                    59,
                    59
                  )
                } else if (RRules.bysetpos) {
                  if (RRules.bysetpos <= 0) {
                    // Get the start of the last week that is relevant for each monthly occurence
                    currentStartDate = new Date(
                      new Date(currentStartDate).getFullYear() + 1,
                      new Date(currentStartDate).getMonth() + 1,
                      RRules.bysetpos - 5
                    )

                    // Get the end of the last week that is relevant for each monthly occurence
                    currentEndDate = new Date(
                      new Date(currentEndDate).getFullYear() + 1,
                      new Date(currentEndDate).getMonth() + 1,
                      RRules.bysetpos + 1
                    )
                  } else {
                    // Get the start of the first week that is relevant for each monthly occurence
                    currentStartDate = new Date(
                      new Date(currentStartDate).getFullYear() + 1,
                      new Date(currentStartDate).getMonth(),
                      RRules.bysetpos * 7 - 6
                    )

                    // Get the end of the first week that is relevant for each monthly occurence
                    currentEndDate = new Date(
                      new Date(currentEndDate).getFullYear() + 1,
                      new Date(currentEndDate).getMonth(),
                      RRules.bysetpos * 7,
                      23,
                      59,
                      59
                    )
                  }
                }

                if (currentStartDate >= new Date(RRules?.dtstart)) count++
              }
              break
            // if the event is based on date
            case 'endBy':
            case 'noEndDate':
              while (currentStartDate <= new Date(RRules?.until)) {
                if (currentStartDate >= new Date(RRules?.dtstart)) {
                  // add a new recurring event for each count
                  newRecurringEvents.push({
                    allDay: event.IsAllDayEvent,
                    daysOfWeek: RRules?.daysOfWeek ? RRules.daysOfWeek : undefined,
                    description: event.Description,
                    end: currentEndDate.toISOString(),
                    endRecur: currentEndDate.toISOString(),
                    etag: event.eTag,
                    eventRRule: event.RRule,
                    extendedProps: {
                      Category: event.Category,
                      IsArchived: event.IsArchived,
                      MAJCOM:
                        metadataContext?.majcomOptions.find(
                          (option) => option.value === event?.MAJCOMCode
                        )?.label || 'Unknown',
                      Section:
                        metadataContext?.sectionOptions.find(
                          (option) => option.value === event?.SectionCode
                        )?.label || 'Unknown',
                    },
                    id: event.eKey,
                    rRule: RRules,
                    start: currentStartDate.toISOString(),
                    startRecur: currentStartDate.toISOString(),
                    title: event.Title,
                  })
                }

                if (RRules.bymonth && RRules.bymonthday) {
                  // increase the current start date by the number month
                  currentStartDate = new Date(
                    new Date(currentStartDate).getFullYear() + RRules.interval,
                    RRules.bymonth - 1,
                    RRules.bymonthday
                  )

                  // increase the current end date by the number month
                  currentEndDate = new Date(
                    new Date(currentEndDate).getFullYear() + RRules.interval,
                    RRules.bymonth - 1,
                    RRules.bymonthday,
                    23,
                    59,
                    59
                  )
                } else if (RRules.bymonthday) {
                  // increase the current start date by the number month
                  currentStartDate = new Date(
                    new Date(currentStartDate).getFullYear() + 1,
                    new Date(currentStartDate).getMonth() + RRules.interval,
                    RRules.bymonthday
                  )

                  // increase the current end date by the number month
                  currentEndDate = new Date(
                    new Date(currentEndDate).getFullYear() + 1,
                    new Date(currentEndDate).getMonth() + RRules.interval,
                    RRules.bymonthday,
                    23,
                    59,
                    59
                  )
                } else if (RRules.bymonth) {
                  // increase the current start date by the number month
                  currentStartDate = new Date(
                    new Date(currentStartDate).getFullYear() + 1,
                    new Date(currentStartDate).getMonth(),
                    RRules.bysetpos * 7 - 6
                  )

                  // increase the current end date by the number month
                  currentEndDate = new Date(
                    new Date(currentEndDate).getFullYear() + 1,
                    new Date(currentEndDate).getMonth(),
                    RRules.bysetpos * 7,
                    23,
                    59,
                    59
                  )
                } else if (RRules.bysetpos) {
                  if (RRules.bysetpos <= 0) {
                    // Get the start of the last week that is relevant for each monthly occurence
                    currentStartDate = new Date(
                      new Date(currentStartDate).getFullYear() + 1,
                      new Date(currentStartDate).getMonth() + 1,
                      RRules.bysetpos - 5
                    )

                    // Get the end of the last week that is relevant for each monthly occurence
                    currentEndDate = new Date(
                      new Date(currentEndDate).getFullYear() + 1,
                      new Date(currentEndDate).getMonth() + 1,
                      RRules.bysetpos + 1,
                      23,
                      59,
                      59
                    )
                  } else {
                    // Get the start of the first week that is relevant for each monthly occurence
                    currentStartDate = new Date(
                      new Date(currentStartDate).getFullYear() + 1,
                      new Date(currentStartDate).getMonth(),
                      RRules.bysetpos * 7 - 6
                    )

                    // Get the end of the first week that is relevant for each monthly occurence
                    currentEndDate = new Date(
                      new Date(currentEndDate).getFullYear() + 1,
                      new Date(currentEndDate).getMonth(),
                      RRules.bysetpos * 7,
                      23,
                      59,
                      59
                    )
                  }
                }
              }
              break
          }
          break
      }
      recurringEvents = [...recurringEvents, ...newRecurringEvents]
    }
  }
  return recurringEvents
}

const convertBooleansToYesNo = (bool: boolean): string => {
  switch (bool) {
    case true:
      return 'Yes'
    case false:
      return 'No'
    default:
      return 'No'
  }
}

const formatPhoneNumber = (number: string) => {
  const cleaned = ('' + number).replace(/\D/g, '')
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
  if (match) {
    return '(' + match[1] + ') ' + match[2] + '-' + match[3]
  }
  return number
}

const generateOptions = (data: { Label: string; Value: number }[]): Dropdown[] => [
  ALL_SELECT_OPTION,
  ...data.map(({ Label, Value }) => ({ label: Label, value: Value })),
]

function removeTags(html: string) {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return doc.body.textContent || ''
}

const encodeHTML = function (str: any) {
  const WHITE_LIST_REGEX = /[^\w. _\-$#/:()&,@;\n\r]/gi
  return str.replace(WHITE_LIST_REGEX, function (c: any) {
    return '&#' + c.charCodeAt(0) + ';'
  })
}
const decodeHTML = function (str: string) {
  const txt = document.createElement('textarea')
  txt.innerHTML = str
  // Split the input string by new lines
  const lines = txt.value.split('\n')
  // Regular expression to match outermost <p> and </p> tags
  const regex = /^<p>(.*)<\/p>$/
  // Process each line to remove outermost <p> and </p> tags
  const processedLines = lines.map((line) => {
    const match = line.match(regex)
    return match ? match[1] : line
  })
  // Join the processed lines back together with new lines
  return processedLines.join('\n')
}
//Returns a Boolean that checks for  spaces and html tags, works in rich text editors
const checkForOnlySpacesOrTags = (val: string): boolean => /^(\s*|<[^>]*>|&nbsp;)*$/.test(val)

/* This highlightMatchingText function is used to highlight the matching text in the given seach*/
function highlightMatchingText(text: string, searchQuery: string): string {
  if (!text || !searchQuery || (Array.isArray(searchQuery) && searchQuery.length === 0)) {
    return text || ''
  }
  let escapedSearchQuery
  if (Array.isArray(searchQuery)) {
    escapedSearchQuery = searchQuery.map(escapeRegExp).join('|')
  } else {
    escapedSearchQuery = escapeRegExp(searchQuery)
  }

  const regex = new RegExp(`(${escapedSearchQuery})`, 'gi')

  return text.replace(
    regex,
    (match: string) => `<span  style="background-color: yellow;"class="highlighted">${match}</span>`
  )
}

function escapeRegExp(text: string) {
  return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')
}
function DocumentIconNames(file: string): string {
  const fileExtension = file?.split('.').pop() ? file?.split('.')?.pop()?.toLowerCase() : ''
  const iconName =
    fileExtension === 'ppt' || fileExtension === 'pptx'
      ? 'icon-pptdoc title-color28'
      : fileExtension === 'pdf'
      ? ' icon-pdf title-color28'
      : fileExtension === 'doc' || fileExtension === 'docx'
      ? 'icon-worddoc ile-icon-word'
      : fileExtension === 'xlsx' || fileExtension === 'xls'
      ? 'icon-excel file-icon-excel'
      : fileExtension === 'txt'
      ? 'icon-emptyfile file-icon-text'
      : fileExtension === 'png' || fileExtension === 'jpg' || fileExtension === 'jpeg'
      ? 'icon-file-picture file-icon-image'
      : fileExtension === 'msg'
      ? 'icon-email'
      : 'icon-file'
  return iconName
}
function ensureNoNullValues(input) {
  const ColumnValue =
    input == undefined || input == null || input == 'null' || input == 'undefined' ? '' : input
  return ColumnValue
}
function isObject(object): boolean {
  return object != null && typeof object === 'object'
}

const isChangeDetected = (object1, object2): boolean => {
  const keys1 = Object.keys(object1)
  const keys2 = Object.keys(object2)

  const obj1 = { ...object1 }
  const obj2 = { ...object2 }
  obj1.eTag = ''
  obj2.eTag = ''

  if (keys1.length !== keys2.length) {
    return true
  }

  for (const key of keys1) {
    if (!keys2.includes(key)) {
      return true
    }

    const val1 = obj1[key]
    const val2 = obj2[key]
    const areObjects = isObject(val1) && isObject(val2)

    if ((areObjects && isChangeDetected(val1, val2)) || (!areObjects && val1 !== val2)) {
      return true
    }
  }

  return false
}
const handleScroll = (e, onTopReachedHandler, onBottomReachedHandler) => {
  // With bottomDeadZone set to 0, the bottom scroll position triggers
  // exactly at the bottom of the scrollbar.  Setting it to a positive
  // integer will trigger the bottom condition slightly before the
  // scrollbar hits the bottom.  The larger the number, the earlier it fires.
  const bottomDeadZone = 0

  const isAtTop = e.target.scrollTop <= 0
  const isAtBottom =
    e.target.scrollHeight - e.target.scrollTop - 5 <= e.target.clientHeight + bottomDeadZone

  if (isAtTop && onTopReachedHandler) onTopReachedHandler()
  if (isAtBottom && onBottomReachedHandler) onBottomReachedHandler()
}

const listAttachments = async (
  listName: string,
  itemKey: string,
  files: File[],
  folderPath: string
) => {
  const list = await mpfRListsHttp().getList(listName)
  const blobFolder = folderPath
  const userDetails = mpfRListsHttp().me()
  const firstName = (await userDetails).firstName
  const lastName = (await userDetails).lastName
  const fullName = `${firstName} ${lastName}`

  for (const file of files) {
    const etag = ''
    const mData = getMetadata(file, fullName)
    await UploadFiles(list, listName, itemKey, mData, etag, file, blobFolder)
  }
}

const exportToExcel = async (workSheetName: string, columns, data) => {
  const workbook = new Excel.Workbook()
  try {
    const worksheet = workbook.addWorksheet(workSheetName)
    worksheet.columns = columns
    worksheet.getRow(1).font = { bold: true }
    worksheet.getRow(1).fill = {
      fgColor: { argb: '838a8a' },
      pattern: 'solid',
      type: 'pattern',
    }
    worksheet.columns.forEach((column) => {
      column.width = (column?.header?.length ?? 0) + 7
      column.alignment = { horizontal: 'center' }
    })

    data.forEach((singleData) => {
      singleData.ItemModified = convertDate(singleData.ItemModified, 'date')
      singleData.ItemCreated = convertDate(singleData.ItemCreated, 'date')
      singleData.ReopenedDate = convertDate(singleData.ReopenedDate, 'date')
      singleData.ResolvedDate = convertDate(singleData.ResolvedDate, 'date')
      singleData.ClosedDate = convertDate(singleData.ClosedDate, 'date')
      singleData.CustomerActionRequiredDate = convertDate(
        singleData.CustomerActionRequiredDate,
        'date'
      )
      worksheet.addRow(singleData)
    })

    worksheet.eachRow({ includeEmpty: false }, (row) => {
      const currentCell = (row as any)._cells
      currentCell.forEach((singleCell) => {
        const cellAddress = singleCell._address
        worksheet.getCell(cellAddress).border = {
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
          top: { style: 'thin' },
        }
      })
    })

    const buf = await workbook.xlsx.writeBuffer()
    saveAs(new Blob([buf]), `${workSheetName}.xlsx`)
  } catch (error) {
    console.error(error)
  } finally {
    workbook.removeWorksheet(workSheetName)
  }
}

/** getUserProfileWithoutID:
 * Finds the User Profile based on a unique identifier
 *
 * @param {string} unqiueIdentifier a unique identifier that can be used to find the user profile (i.e. DoDNumber or DutyEmail)
 * @param {string} filterByCol defines what column the uniqueIdentifier will be found in
 * @returns the user's profile
 */
const getUserProfileWithoutID = async (unqiueIdentifier: string, filterByCol: string) => {
  let userProfile = {}
  const listName = 'UserProfileList'

  const list = await mpfRListsHttp().getList(listName)
  const endpoint = [
    'AssignedComponentCode',
    'UserRID',
    'DoDNumber',
    'ProfileStatus',
    'Rank',
    'DutyEmail',
    'DutyPhone',
    'UserDisplayName',
    'PascodeID',
    'PersonalEmail',
    'PersonalPhone',
    'PersonalPhoneProvider',
    'PASCODEMetadata/PASCODE',
    'PASCODEMetadata/Installation',
    'PASCODEMetadata/Organization',
    'PASCODEMetadata/ServicingBy',
    'PASCODEMetadata/OrgMAJCOM',
    'PASCODEMetadata/ServicingMAJCOM',
    'PASCODEMetadata/ServicingOrganization',
    'eKey',
    'eTag',
  ]

  const profileData = await list
    .query()
    .filter(`${filterByCol} eq '${unqiueIdentifier}'`)
    .select(...endpoint)
    .expand('PASCODEMetadata')
    .orderBy([{ ascending: false, orderBy: 'PascodeID' }])
    .top(1)
    .get()

  if (profileData && [...profileData].length > 0) {
    userProfile = {
      AssignedComponentCode: profileData[0].AssignedComponentCode || '',
      CustomerStatus: profileData[0].ProfileStatus || '',
      DoDNumber: profileData[0].DoDNumber || '',
      DutyEmail: profileData[0].DutyEmail || '',
      DutyPhone: profileData[0].DutyPhone || '',
      Installation: profileData[0]['PASCODEMetadata/Installation'] || '',
      OrgMAJCOM: profileData[0]['PASCODEMetadata/OrgMAJCOM'] || '',
      Organization: profileData[0]['PASCODEMetadata/Organization'] || '',
      PASCODE: profileData[0]['PASCODEMetadata/PASCODE'] || '',
      PASCODEServicedBy: profileData[0]['PASCODEMetadata/ServicingBy'] || '',
      PascodeID: profileData[0].PascodeID || -1,
      PersonalEmail: profileData[0].PersonalEmail || '',
      PersonalPhoneNumber: profileData[0].PersonalPhone || '',
      PersonalPhoneProvider: profileData[0].PersonalPhoneProvider || '',
      Rank: profileData[0].Rank || '',
      ServicingMAJCOM: profileData[0]['PASCODEMetadata/ServicingMAJCOM'] || '',
      ServicingOrganization: profileData[0]['PASCODEMetadata/ServicingOrganization'] || '',
      UserRID: profileData[0].UserRID || '',
      eKey: profileData[0].eKey || '',
      eTag: profileData[0].eTag || '',
    }
  }

  return userProfile
}

const filterRank = (status: string, rankmetadata, userSelectedComponent) => {
  const filteredRanks = rankmetadata
    ?.filter(
      (metadata) =>
        metadata.Status === status && metadata.AssignedComponentCode === userSelectedComponent
    )
    .map(({ ShortName }) => {
      return { label: ShortName, value: ShortName }
    })
  return filteredRanks
}

// this date conversion function is temporarily here -- it will be replaced by
// a common function in assets/ts/data-and-time that is being introduced in a
// parallel PR but not yet available
const toShortDateString = (date: Date | string | undefined): string => {
  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }

  const dateObj = typeof date === 'object' ? date : typeof date === 'string' ? new Date(date) : null

  const dateString = dateObj ? dateObj.toLocaleDateString('en-US', options) : ''

  return dateString
}

const getFilteredDates = (filter: string) => {
  const todayDate = new Date()
  const splitedFither = filter.split(' ')
  const lastOrNext = splitedFither[0]
  const lastOrNextNumber: number = splitedFither.length === 4
    ? parseInt(splitedFither[2])
    : parseInt(splitedFither[1])
  const lastOrNextDays = splitedFither[2]

  if (lastOrNextDays === 'hours' && lastOrNext === 'Last') {
     todayDate.setHours(todayDate.getHours() - lastOrNextNumber)
     return todayDate.toISOString()
  } else if (lastOrNextDays === 'days' && lastOrNext === 'Last') {
     todayDate.setDate(todayDate.getDate() - lastOrNextNumber)
     return todayDate.toISOString()
  } else if (lastOrNext === 'Next') {
    todayDate.setDate(todayDate.getDate() + lastOrNextNumber)
    return todayDate.toISOString()
  } else if (lastOrNext === 'More') {
     todayDate.setDate(todayDate.getDate() - lastOrNextNumber)
     return todayDate.toISOString()
  } else{
    return ALL_SELECT_VALUE_STRING
  }
}
/**
 * Generates a null value if input is -1 else returns the code value.
 * Used to prevent -1 value from going to the backend which can cause exceptions
 *
 * @param {number} code - The input number used to generate the code.
 * @return {number | null} - The generated code if the input number is greater than 0, otherwise null.
 */
function generateCode(code: number) {
  return code > 0 || code === undefined ? code : null
}

function getMAJCOMCode(name: string, majcoms: object): number {
  const majcomCode = (majcoms as any).find((item) => item.Label === name)?.Value
  return majcomCode
}

function getMAJCOMString(code: number, majcoms: object) {
  return (majcoms as any).find((item) => item.Value === code)?.MAJCOM
}

const updateInvolvedUsers = (inquiryData, userID) => {
  let involvedUsers = [] // array of user IDs

  // inquiryData.PreviouslyInvolvedUsers: Array of objects; props: Id (Integer), Title (String)
  if (inquiryData?.InvolvedUsers?.length > 0) {
    let i = 0

    inquiryData.InvolvedUsers.forEach((user) => {
      if (userID !== user) {
        involvedUsers = involvedUsers.concat(user) // keep the previously involved users
      } else {
        inquiryData.InvolvedUsers[i] = '' // remove the current user's ID from the Involved Users info
      }
      i++
    })
  }

  // if the currently assigned user is not in the involved users & we are not assigning to other section, or we are assigning to other section with response required

  involvedUsers = involvedUsers.concat(userID) // add the currently assigned user to the involved users before changing the assignTo id

  involvedUsers = involvedUsers.filter((i) => i) // remove empty strings

  return involvedUsers
}
function removeFirstStringFromSecond(firstString, secondString) {
  // Check if the first string exists within the second string
  if (secondString?.startsWith(firstString)) {
    // Remove the first string and trim any leading whitespace
    return secondString?.slice(firstString.length)?.trim()
  }
  // If the first string is not at the start of the second string, return the original second string
  return secondString
}
const convertSiteAdmin = (data, priorInquiry) => {
  const { CurrentlyServicingBy, PreviousStatusCode, StatusCode } = data
  const isSubmittedOrReopened = StatusCode === 'SUBMITTED' || StatusCode === 'REOPENED'
  if (isSubmittedOrReopened || PreviousStatusCode === 'REOPENED') {
    if (CurrentlyServicingBy === 'CSS') {
      return 'CSS'
    }
    if (CurrentlyServicingBy === 'MPF') {
      const sectionValue = data['Section/MetadataValue'] || data['SectionValue']

      if (
        (StatusCode === 'REOPENED' && PreviousStatusCode === 'RESOLVED') ||
        (StatusCode === 'RESOLVED' && PreviousStatusCode === 'REOPENED')
      ) {
        return priorInquiry
          ? removeFirstStringFromSecond(data['Section/MetadataValue'], priorInquiry.UserRole)
          : ''
      } else {
        return `${sectionValue} Technician`
      }
    }
  }
  if 
  (StatusCode === 'RESOLVED') {
    return removeFirstStringFromSecond(data['Section/MetadataValue'], priorInquiry?.UserRole)
  } else if (!isSubmittedOrReopened) {
    return data['Status/StatusDescription']?.replace('-', ' ')
  }
  return null // Default case
}

const handleRoute = (cancel, setShowConfirmReload) => {
  if (cancel == 'cancel') {
    setShowConfirmReload(false)
  } else {
    window.location.reload()
  }
}

const formattedNotificationText = (notification, data, me) => {
  return notification.AlertText
    ? notification.AlertText.replace('[INQUIRY_ID]', data.InquiryID || '')
        .replace('[DUE_OUT_ID]', data.DueOutID || '')
        .replace('[DOC_NAME]', data.DocName)
        .replace('[COMMENT_SUBMITTER]', `${me.firstName} ${me.lastName}`)
        .replace(
          '[ASSIGNED_TO_USERNAME]',
          `${data?.AssignedTo?.properties?.firstName} ${
            data?.AssignedTo?.properties?.lastName || ''
          }`
        )
        .replace('[ADDED_BY_USERNAME]', me.persona || `${me.firstName} ${me.lastName}` || '')
        .replace('[CANCELED_BY_USERNAME]', me.persona || '')
        .replace('[REMOVED_BY_USERNAME]', me.persona || `${me.firstName} ${me.lastName}` || '')
        .replace('[ELEVATED_BY_USERNAME]', me.persona || '')
        .replace('[RESPOND_USERNAME]', me.persona || '')
        .replace('[CHANGE_SECTION_USERNAME]', `${data?.newSection} Technician` || '')
    : ''
}

const getToUsers = (notification, data, me) => {
  const assignedToUserRID = data?.AssignedTo?.userID ? data.AssignedTo.userID : ''
  const toUsers: any[] = []
  let returnRole = false
  let role = ''

  if (notification?.ToUserRoles) {
    notification.ToUserRoles.forEach((userRole) => {
      switch (userRole) {
        case 'AssignedTo':
          assignedToUserRID !== '' && toUsers.push(assignedToUserRID)
          break
        case 'AssignedToIfNotSame':
          assignedToUserRID !== '' &&
            assignedToUserRID !== me.UserRID &&
            toUsers.push(assignedToUserRID)
          break
        case 'ActiveUsersExcludingAssignedUser':
          data.InvolvedUsers &&
            data.InvolvedUsers.length > 0 &&
            data.InvolvedUsers.forEach((userRID) => {
              userRID && userRID !== assignedToUserRID && toUsers.push(userRID)
            })
          break
        case 'ParentUsers':
          data.parentUser !== '' && toUsers.push(data.parentUser)
          break
        default:
          returnRole = true
          role = userRole.replace(
            '[SECTION_NAME]',
            `${data.ServicingOrganization}-${data['Section/MetadataValue']}`
          )
      }
    })
  }

  return returnRole ? role : [...new Set(toUsers)]
}
const generateCodeOptionOnLength = (
  metadataOptions: optionsTypes[],
  codes: number[] | undefined
) => {
  const options = metadataOptions.filter((option) => codes?.includes(option.value as number))
  return options.length === 1 ? options : [ALL_SELECT_OPTION, ...options]
}
const getFilteredItems = (metadata: PascodeMetadata[], organizations: string[]) =>
  metadata.filter((item) => organizations?.includes(item.ServicingOrganization))
const getUniqueCodes = (filteredItems: PascodeMetadata[]) => {
  const servicingMAJCOMS = new Set()
  const componentCodes = new Set()
  const organizations = new Set()

  filteredItems.forEach((item) => {
    servicingMAJCOMS.add(item.ServicingMAJCOM)
    componentCodes.add(item.AssignedComponentCode)
    organizations.add(item.ServicingOrganization)
  })

  return {
    componentCodes: [...componentCodes] as number[],
    organizations: [...organizations] as string[],
    servicingMAJCOMS: [...servicingMAJCOMS] as string[],
  }
}
export const sendNotification = async (ActivityID, data, me, userRole, isDueOut) => {
  let activityID = ActivityID
  if (ActivityID === 'ASSIGN_TO_SECTION') activityID = 'CHANGE_SECTION'
  const list: any = await mpfRListsHttp().getList('NotificationsMetadataList')
  const notificationList: any = await mpfRListsHttp().getList('NotificationsList')

  const promiseArr: any[] = []

  const notificationMetadata: any[] = await list.query().get()

  const filteredNotificationMetadata: any[] = notificationMetadata.filter((metadata) => {
    const splitUserRole = userRole.split(' ')
    const formattedUserRole =
      splitUserRole[splitUserRole.length - 1] === 'Chief'
        ? `Section Chief`
        : splitUserRole[splitUserRole.length - 1]

    return (
      metadata.ActivityID === activityID &&
      formattedUserRole.includes(metadata.Role) &&
      isDueOut === metadata.IsDueOut &&
      (activityID !== 'ELEVATE' ||
        (activityID === 'ELEVATE' &&
          data?.ElevatedUserRole?.split('-')[1]?.split('_')?.join(' ')?.toLowerCase() ===
            metadata?.ToUserRoles[0]?.split('-')[1]?.toLowerCase()))
    )
  })

  filteredNotificationMetadata.forEach((notification) => {
    const AlertText = formattedNotificationText(notification, data, me)
    const ToUsers = getToUsers(notification, data, me)
    if (AlertText && ToUsers.length > 0) {
      promiseArr.push(
        notificationList.addOrUpdateItem({
          ActivityID: activityID,
          AlertText: AlertText,
          AlertType: notification.AlertType,
          DataGUID: isDueOut ? data.DueOutGUID : data.InquiryGUID,
          DataID: isDueOut ? data.DueOutID : data.InquiryID,
          FromUser: me.userId,
          IsDueOut: isDueOut,
          IsRead: false,
          ItemCreated: new Date(),
          ItemCreatedBy: me.userId,
          ItemModified: new Date(),
          ItemModifiedBy: me.userId,
          Role: notification.Role,
          To: ToUsers.toString(),
        })
      )
    }
  })

  await Promise.all(promiseArr)
}

export const sendEmail = async (emailObj) => {
  let body = ''
  let clickHereText = ''
  if (emailObj.body?.includes('&;')) {
    clickHereText = emailObj.body?.split('&;')[1]
    clickHereText = clickHereText?.split('[Click Here]')[1]
  } else {
    clickHereText = emailObj.body
    if (clickHereText?.indexOf('[Click Here]') > -1)
      clickHereText = clickHereText?.split('[Click Here]')[1]
  }
  if (emailObj.isDiscussion) {
    body = emailObj.Discussion
  } else if (emailObj.isSurvey) {
    body = emailObj.bodySurvey
  } else {
    body = emailObj.body
  }

  const request = {
    bcc: [emailObj.bcc],
    body: emailObj.isDiscussion ? emailObj.discussion : body?.replace(/\n/g, ''),
    cc: [emailObj.cc],
    isHtml: true,
    subject: emailObj.subject,
    to: [emailObj.to],
  }

  const notifyID = await mpfNotifyHttp().Send(request)
  if (notifyID > 1) {
    await addToEmailLogList(emailObj, body, true, 'Success')
  } else {
    await addToEmailLogList(emailObj, body, false, 'Error')
  }
}

export const formatBody = (type, data, navURL) => {
  let body = ''
  let clickHereText = ''
  let hasBody = true

  body += '<!doctype html>'
  body += '<HTML><HEAD> <META name=GENERATOR content="MSHTML 11.00.10570.1001"></HEAD> <BODY>'
  body +=
    '<TABLE style="FONT-SIZE: 13px; FONT-FAMILY: Segoe, Segoe UI, DejaVu Sans, Trebuchet MS, Verdana,\' sans-serif\'; FONT-WEIGHT: 600; MARGIN: 0px auto" cellSpacing=0 cellPadding=0 width=620 border=0>'
  body += '<TBODY>'
  body +=
    '<TR><TD style="BORDER-COLLAPSE: collapse; PADDING-BOTTOM: 10px; PADDING-TOP: 10px; PADDING-LEFT: 15px; MARGIN: 0px; BACKGROUND-COLOR: #efeff7" vAlign=top border="0" cellpadding="0" cellspacing="0">'
  body +=
    '<H1 style="FONT-SIZE: 18px; MARGIN-BOTTOM: 0px; FONT-FAMILY: Arial, Helvetica, sans-serif; MARGIN-TOP: 0px; FONT-WEIGHT: bold; COLOR: #00529b; PADDING-BOTTOM: 0px; PADDING-TOP: 0px">U.S. AIR FORCE</H1>'
  body +=
    '<H2 style="FONT-SIZE: 14px; MARGIN-BOTTOM: 0px; FONT-FAMILY: Arial, Helvetica, sans-serif; MARGIN-TOP: 0px; FONT-WEIGHT: bold; COLOR: #232324; PADDING-BOTTOM: 0px; PADDING-TOP: 0px">MPF Customer Portal</H2>'
  body += '</TD></TR>'
  body += '<TR><TD>'
  body +=
    '<TABLE style="BORDER-RIGHT: #B7B7B7 1px solid; BORDER-LEFT: #B7B7B7 1px solid" cellSpacing=0 cellPadding=0 width=620>'
  body += '<TBODY>'
  switch (type) {
    case 'Discussion':
    case 'Reply':
      body +=
        '<TR><TD style="FONT-SIZE: 13px; FONT-FAMILY: Segoe, Segoe UI, DejaVu Sans, Trebuchet MS, Verdana,\' sans-serif\'; BORDER-RIGHT: #efeff7 1px solid; COLOR: #323232; PADDING-BOTTOM: 10px; PADDING-TOP: 10px; PADDING-LEFT: 10px; PADDING-RIGHT: 10px">'
      body +=
        '<TABLE style="FONT-SIZE: 13px; FONT-FAMILY: Segoe, Segoe UI, DejaVu Sans, Trebuchet MS, Verdana,\' sans-serif\'" cellSpacing=0 cellPadding=0 width=620>'
      body += '<TBODY>'
      body +=
        '<TR><TD style="FONT-SIZE: 14px; FONT-FAMILY: Segoe, Segoe UI, DejaVu Sans, Trebuchet MS, Verdana,\' sans-serif\'; BORDER-BOTTOM: #88a6db 1px solid; COLOR: #88a6db; PADDING-BOTTOM: 5px">Subject'
      body += '</TD></TR>'
      body +=
        '<TR><TD style="FONT-SIZE: 12px; FONT-FAMILY: Segoe, Segoe UI, DejaVu Sans, Trebuchet MS, Verdana,\' sans-serif\'; COLOR: #333333; PADDING-BOTTOM: 5px; PADDING-TOP: 5px">'
      body += '' + data.Title + ''
      body += '</TD></TR>'
      body +=
        '<TR><TD style="FONT-SIZE: 14px; FONT-FAMILY: Segoe, Segoe UI, DejaVu Sans, Trebuchet MS, Verdana,\' sans-serif\'; BORDER-BOTTOM: #c8a2cd 1px solid; COLOR: #c8a2cd; PADDING-BOTTOM: 5px">Comment'
      body += '</TD></TR>'
      body +=
        '<TR> <TD style="FONT-SIZE: 12px; FONT-FAMILY: Segoe, Segoe UI, DejaVu Sans, Trebuchet MS, Verdana,\' sans-serif\'; COLOR: #333333; PADDING-BOTTOM: 5px; PADDING-TOP: 5px">'
      body += '<P> ' + data.comment + ' </P><P> ' + data.bodyText + ' </P>'
      body += '</TD></TR>'
      body += '</TBODY>'
      body += '</TABLE>'
      body += '</TD></TR>'
      clickHereText = 'to view the comment(s)'
      break
    case 'survey':
      body +=
        '<TR><TD style="FONT-SIZE: 13px; FONT-FAMILY: Segoe, Segoe UI, DejaVu Sans, Trebuchet MS, Verdana,\' sans-serif\'; BORDER-RIGHT: #efeff7 1px solid; COLOR: #323232; PADDING-BOTTOM: 10px; PADDING-TOP: 10px; PADDING-LEFT: 10px; PADDING-RIGHT: 10px">'
      body +=
        '<TABLE style="FONT-SIZE: 13px; FONT-FAMILY: Segoe, Segoe UI, DejaVu Sans, Trebuchet MS, Verdana,\' sans-serif\'" cellSpacing=0 cellPadding=0 width=620>'
      body += '<TBODY>'
      body += '' + data.bodyText + ''
      body += '</TBODY>'
      body += '</TABLE>'
      body += '</TD></TR>'
      clickHereText = `to complete the survey. Thank you for your time.`
      break
    case 'Respond':
      clickHereText = 'to view the comment(s)'
      break
    case 'Resolve':
    case 'Close':
    case 'CloseDueout':
    case 'SubmitInquiry':
    case 'UpdateInquiry':
    case 'SubmitDueOut':
    case 'UpdateDueOut':
    case 'Complete':
    case 'Cancel':
      body +=
        '<TR><TD style="FONT-SIZE: 13px; FONT-FAMILY: Segoe, Segoe UI, DejaVu Sans, Trebuchet MS, Verdana,\' sans-serif\'; BORDER-RIGHT: #efeff7 1px solid; COLOR: #323232; PADDING-BOTTOM: 10px; PADDING-TOP: 10px; PADDING-LEFT: 10px; PADDING-RIGHT: 10px">'
      body +=
        '<TABLE style="FONT-SIZE: 13px; FONT-FAMILY: Segoe, Segoe UI, DejaVu Sans, Trebuchet MS, Verdana,\' sans-serif\'" cellSpacing=0 cellPadding=0 width=620>'
      body += '<TBODY>'
      body += '' + data.bodyText + ''
      body += '</TBODY>'
      body += '</TABLE>'
      body += '</TD></TR>'
      clickHereText = `to view the ${data.isInquiry ? 'Inquiry' : 'Due Out'}.`
      break
      case 'CustomerAction':
      body +=
        '<TR><TD style="FONT-SIZE: 13px; FONT-FAMILY: Segoe, Segoe UI, DejaVu Sans, Trebuchet MS, Verdana,\' sans-serif\'; BORDER-RIGHT: #efeff7 1px solid; COLOR: #323232; PADDING-BOTTOM: 10px; PADDING-TOP: 10px; PADDING-LEFT: 10px; PADDING-RIGHT: 10px">'
      body +=
        '<TABLE style="FONT-SIZE: 13px; FONT-FAMILY: Segoe, Segoe UI, DejaVu Sans, Trebuchet MS, Verdana,\' sans-serif\'" cellSpacing=0 cellPadding=0 width=620>'
      body += '<TBODY>'
      body += '' + data.bodyText + ''
      body += '</TBODY>'
      body += '</TABLE>'
      body += '</TD></TR>'
      clickHereText = `to view the required action.`
      break
      case 'Document':
      body +=
        '<TR><TD style="FONT-SIZE: 13px; FONT-FAMILY: Segoe, Segoe UI, DejaVu Sans, Trebuchet MS, Verdana,\' sans-serif\'; BORDER-RIGHT: #efeff7 1px solid; COLOR: #323232; PADDING-BOTTOM: 10px; PADDING-TOP: 10px; PADDING-LEFT: 10px; PADDING-RIGHT: 10px">'
      body +=
        '<TABLE style="FONT-SIZE: 13px; FONT-FAMILY: Segoe, Segoe UI, DejaVu Sans, Trebuchet MS, Verdana,\' sans-serif\'" cellSpacing=0 cellPadding=0 width=620>'
      body += '<TBODY>'
      body += '' + data.bodyText + ''
      body += '</TBODY>'
      body += '</TABLE>'
      body += '</TD></TR>'
      clickHereText = `to view the file(s).`
      break
    case 'AssignTo':
      body +=
        '<TR><TD style="FONT-SIZE: 13px; FONT-FAMILY: Segoe, Segoe UI, DejaVu Sans, Trebuchet MS, Verdana,\' sans-serif\'; BORDER-RIGHT: #efeff7 1px solid; COLOR: #323232; PADDING-BOTTOM: 10px; PADDING-TOP: 10px; PADDING-LEFT: 10px; PADDING-RIGHT: 10px">'
      body +=
        '<TABLE style="FONT-SIZE: 13px; FONT-FAMILY: Segoe, Segoe UI, DejaVu Sans, Trebuchet MS, Verdana,\' sans-serif\'" cellSpacing=0 cellPadding=0 width=620>'
      body += '<TBODY>'
      body += '' + data.bodyText + ''
      body += '</TBODY>'
      body += '</TABLE>'
      body += '</TD></TR>'
      clickHereText = `to view the comment(s).`
      break
    case 'TeamMember':
      clickHereText = `to view the details.`
      hasBody = false
      break
    case 'PendingActionOutside':
    case 'OutsideActionComplete':
      body +=
        '<TR><TD style="FONT-SIZE: 13px; FONT-FAMILY: Segoe, Segoe UI, DejaVu Sans, Trebuchet MS, Verdana,\' sans-serif\'; BORDER-RIGHT: #efeff7 1px solid; COLOR: #323232; PADDING-BOTTOM: 10px; PADDING-TOP: 10px; PADDING-LEFT: 10px; PADDING-RIGHT: 10px">'
      body +=
        '<TABLE style="FONT-SIZE: 13px; FONT-FAMILY: Segoe, Segoe UI, DejaVu Sans, Trebuchet MS, Verdana,\' sans-serif\'" cellSpacing=0 cellPadding=0 width=620>'
      body += '<TBODY>'
      body += '' + data.bodyText + ''
      body += '</TBODY>'
      body += '</TABLE>'
      body += '</TD></TR>'

      clickHereText = `to view the details.`
      break
    case 'DeleteTeamMember':
    case 'CustomerActionComplete':
      body +=
        '<TR><TD style="FONT-SIZE: 13px; FONT-FAMILY: Segoe, Segoe UI, DejaVu Sans, Trebuchet MS, Verdana,\' sans-serif\'; BORDER-RIGHT: #efeff7 1px solid; COLOR: #323232; PADDING-BOTTOM: 10px; PADDING-TOP: 10px; PADDING-LEFT: 10px; PADDING-RIGHT: 10px">'
      body +=
        '<TABLE style="FONT-SIZE: 13px; FONT-FAMILY: Segoe, Segoe UI, DejaVu Sans, Trebuchet MS, Verdana,\' sans-serif\'" cellSpacing=0 cellPadding=0 width=620>'
      body += '<TBODY>'
      body += '' + data.bodyText + ''
      body += '</TBODY>'
      body += '</TABLE>'
      body += '</TD></TR>'
      clickHereText = `to view the details.`
      break
  }
  body += `<TR> <TD style="FONT-SIZE: 12px; FONT-FAMILY: Segoe, Segoe UI, DejaVu Sans, Trebuchet MS, Verdana,' sans-serif'; ${
    hasBody && 'BORDER-TOP: #B7B7B7 1px solid;'
  } COLOR: #555555; PADDING-BOTTOM: 10px; PADDING-TOP: 10px; PADDING-LEFT: 10px; PADDING-RIGHT: 10px">`
  body +=
    '' +
    'Please <a style="FONT-SIZE: 12px; TEXT-DECORATION: none; VERTICAL-ALIGN: top; FONT-WEIGHT: 600; COLOR: #199059; MARGIN-LEFT: 2px; MARGIN-RIGHT: 2px" href=\'' +
    navURL +
    `'>Click Here</a> ${clickHereText}` +
    ''
  body += '</TD></TR>'
  body += `<tr><td style="font-size: 12px; border-top: #B7B7B7 1px solid; font-family: Segoe, Segoe UI, DejaVu Sans, Trebuchet MS, Verdana,' sans-serif'; border-right: #B7B7B7 1px solid;border-left: #B7B7B7 1px solid; border-bottom: #B7B7B7 1px solid; color: #555555; padding-bottom: 10px; padding-top: 10px; padding-left: 10px; padding-right: 10px">PLEASE DO NOT REPLY. This mailbox is unmonitored, if you have any questions, please contact: Local MPF Team.</td></tr></tbody></table></td></tr></tbody></table></body></html>`
  body += '</TD></TR></TBODY></TABLE>'
  body += '</TD></TR></TBODY></TABLE></BODY></HTML>'
  return body
}

export const addToEmailLogList = async (emailObj, body, emailSent, errorInfo) => {
  const LIST_NAME = 'EmailLog'
  const emailList = await mpfRListsHttp().getList(LIST_NAME)
  const SITEFEEDBACK_MAILTITLE = 'Sitefeedback'
  const EmailLogCols = {
    Action: 'Action',
    Body: 'Body',
    EKey: 'eKey',
    ETag: 'eTag',
    ErrorInformation: 'ErrorInformation',
    From: 'From',
    ID: 'ID',
    IsMailSent: 'IsMailSent',
    ItemCreated: 'ItemCreated',
    ItemCreatedBy: 'ItemCreatedBy',
    ItemGUID: 'ItemGUID',
    ItemID: 'ItemID',
    ItemModified: 'ItemModified',
    ItemModifiedBy: 'ItemModifiedBy',
    Subject: 'Subject',
    Title: 'Title',
    To: 'To',
  }

  const addObj: any = {}
  if (emailObj.action === SITEFEEDBACK_MAILTITLE) {
    addObj[EmailLogCols.Title] = SITEFEEDBACK_MAILTITLE
    addObj[EmailLogCols.Action] = SITEFEEDBACK_MAILTITLE
  } else {
    addObj[EmailLogCols.Title] = emailObj?.data?.Title ? emailObj?.data?.Title : ''
    addObj[EmailLogCols.Action] = emailObj?.data?.Action ? emailObj?.data?.Action : ''
  }

  addObj[EmailLogCols.ItemID] = emailObj?.data?.ItemID ? emailObj?.data?.ItemID : ''
  addObj[EmailLogCols.ItemGUID] = emailObj?.data?.ItemGUID ? emailObj?.data?.ItemGUID : ''
  addObj[EmailLogCols.From] = 'no-reply@sharepointonline.com'
  addObj[EmailLogCols.To] = emailObj?.to
  addObj[EmailLogCols.Subject] = emailObj?.subject
  addObj[EmailLogCols.IsMailSent] = emailSent
  addObj[EmailLogCols.ItemCreated] = new Date()
  addObj[EmailLogCols.ItemModified] = new Date()
  addObj[EmailLogCols.ItemCreatedBy] = emailObj?.loginuserId
  addObj[EmailLogCols.ItemModifiedBy] = emailObj?.loginuserId
  addObj[EmailLogCols.Body] = body
  addObj[EmailLogCols.ErrorInformation] = errorInfo === 'Error' ? 'error' : ''
  try {
    await emailList.addOrUpdateItem(addObj)
  } catch (error) {
    console.error(error.name)
  }
}

export function filterBy(value: number | string, filter: number | string) {
  return (
    filter === -1 ||
    filter === '' ||
    (Array.isArray(value) && (value.length <= 0 || value?.some((val) => val === filter))) ||
    value === filter ||
    value === -1 ||
    value === '' ||
    value === null
  )
}
export const generateSquadronOptions = (squadrons: string[]) => {
  const squdronsOptions = squadrons.map((item) => ({
    label: item.replace('MPF', 'FSS'),
    value: item.replace('MPF', 'FSS'),
  }))
  return squdronsOptions.length === 0
    ? [NONE_SELECT_OPTION_STRING]
    : squdronsOptions.length === 1
    ? squdronsOptions
    : [ALL_SELECT_OPTION_STRING, ...squdronsOptions]
}
export const getSquadrons = (
  pascodeMetadata: PascodeMetadata[],
  profileComponentCode?: number,
  assignComponentCodes?: number[],
  profileMajcomCode?: string,
  servicingMAJCOMs?: string[],
  organizations?: string[]
): string[] => {
  const filterCondition: (item: any) => boolean = ({
    AssignedComponentCode,
    ServicingMAJCOM,
    ServicingOrganization,
  }) => {
    if (profileMajcomCode && organizations) {
      return ServicingMAJCOM === profileMajcomCode && organizations.includes(ServicingOrganization)
    }
    if (profileComponentCode) {
      return AssignedComponentCode === profileComponentCode
    }
    if (assignComponentCodes && profileMajcomCode) {
      return (
        assignComponentCodes.includes(AssignedComponentCode) &&
        ServicingMAJCOM === profileMajcomCode
      )
    }
    if (assignComponentCodes) {
      return assignComponentCodes.includes(AssignedComponentCode)
    }
    if (profileMajcomCode) {
      return ServicingMAJCOM === profileMajcomCode
    }
    if (servicingMAJCOMs) {
      return servicingMAJCOMs.includes(ServicingMAJCOM)
    }
    return true
  }
  const squadrons = [
    ...new Set(
      pascodeMetadata
        .filter(filterCondition)
        .map(({ ServicingOrganization }) => ServicingOrganization)
    ),
  ].sort()

  return squadrons
}

export const generateCategoryOptions = (
  sectionLabel: string | undefined,
  categories: MetadataContextInterface['categories'],
  sections: MetadataContextInterface['sections']
): optionsTypes[] => {
  if (!sectionLabel) return []

  const section = sections.find((metadata) => metadata.Label === sectionLabel)
  if (!section) return []

  const filteredCategories = categories.filter(
    (item) => item.ParentSectionCode === section.SectionCode
  )
  return [
    ...filteredCategories.map(({ Label, Value }) => ({
      label: Label,
      value: Value,
    })),
  ]
}

export const CATEGORY_COLORS_ENUM = {
  Accounting: '#546E7A',
  ActionChartEightDays: '#3C4A9B',
  ActionChartFourDays: '#A9B23F',
  ActionChartTenDays: '#b17320',
  ActionChartThreeDays: '#81741A',
  Active: '#C3711F',
  Budget: '#3C4A9B',
  'Civilian Pay': '#0661A3',
  Dissatisfied: '#f7a711',
  'Military Pay': '#00C695',
  Negative: '#f51b20',
  Neutral: '#ffc62f',
  Positive: '#3eaa24',
  Reopened: '#5C7296',
  Satisfied: '#8dc63f',
  'Somewhat Negative': '#f7a711',
  'Somewhat Positive': '#8dc63f',
  Submitted: '#2DACD3',
  'Travel Pay': '#6BB553',
  'Very Dissatisfied': '#f51b20',
  'Very Satisfied': '#3eaa24',
}
export {
  DocumentIconNames,
  FILE_CHARACTERS_UNALLOWED,
  FilteredDatesType,
  checkForOnlySpacesOrTags,
  convertBooleansToYesNo,
  convertSiteAdmin,
  decodeHTML,
  encodeHTML,
  ensureNoNullValues,
  escapeRegExp,
  exportToExcel,
  filterRank,
  formatPhoneNumber,
  generateCode,
  generateCodeOptionOnLength,
  generateOptions,
  getFilteredDates,
  getFilteredItems,
  getMAJCOMCode,
  getMAJCOMString,
  getRecurringEventData,
  getUniqueCodes,
  getUserProfileWithoutID,
  handleRoute,
  handleScroll,
  highlightMatchingText,
  isChangeDetected,
  listAttachments,
  removeTags,
  scrollToTop,
  toShortDateString,
  updateInvolvedUsers,
}

export const displayAriaLabel = (firstName: string, lastName: string, modifiedTime: any) => {
  return firstName + '' + lastName + '|' + formatDate(modifiedTime)
}
